// ---------------------------------------------------------------------------
// Application-wide constants
// ---------------------------------------------------------------------------

// External URLs
export const CLOUDBEDS_BOOKING_URL =
  "https://hotels.cloudbeds.com/reservation/SQuKvJ";

export const SITE_URL =
  import.meta.env.VITE_SITE_URL ?? "https://schoendorfhostel.com";

/** Base URL of the guest-frontend (OTA guest area). Empty = same domain. */
export const GUEST_AREA_BASE_URL =
  import.meta.env.VITE_GUEST_AREA_BASE_URL ?? "";

// ---------------------------------------------------------------------------
// Room types
// ---------------------------------------------------------------------------

export interface RoomType {
  id: string;
  name: string;
  slug: string;
  capacity: number;
  description: string;
  /** Longer room details for the detail modal */
  details: string;
  features: string[];
  /** Regex to match Cloudbeds roomTypeName → this display config */
  namePattern: RegExp;
  isDorm: boolean;
  /** Primary room photo path relative to BASE_URL */
  image: string;
  /** All room photos (first is the primary) */
  images: string[];
}

export const ROOM_TYPES: RoomType[] = [
  {
    id: "1",
    name: "12-Bed Mixed Dorm",
    slug: "12-bed-mixed-dorm",
    capacity: 12,
    description:
      "Spacious 12-bed mixed dorm with privacy curtains, personal lights, and individual lockers. Beds 101–112.",
    details:
      "Our largest dorm offers 12 sturdy bunk beds (beds 101–112), each with a thick privacy curtain, personal reading light, USB charging port, and a shelf for your belongings. Every guest gets a secure full-size locker. Shared bathroom with multiple showers is just steps away. Located on the ground floor with easy access to the common room and kitchen.",
    features: ["Privacy curtains", "Locker", "Reading light", "USB charging"],
    namePattern: /12.*bed|101.*112/i,
    isDorm: true,
    image: "images/dorm-bunks-curtains.jpg",
    images: [
      "images/dorm-bunks-curtains.jpg",
      "images/dorm-main.jpg",
      "images/dorm-hallway.jpg",
      "images/dorm-bunk-locker.jpg",
      "images/dorm-lockers.jpg",
      "images/bathroom-showers.jpg",
    ],
  },
  {
    id: "2",
    name: "6-Bed Mixed Dorm",
    slug: "6-bed-mixed-dorm",
    capacity: 6,
    description:
      "Cosy 6-bed mixed dorm with privacy curtains and personal storage.",
    details:
      "A more intimate dorm with just 6 beds — ideal if you want the social hostel experience with a bit more space. Each bed has a privacy curtain, reading light, and USB port. Full-size lockers keep your belongings safe. Shared bathroom with walk-in showers nearby.",
    features: ["Privacy curtains", "Locker", "Spacious", "USB charging"],
    namePattern: /6[-\s]*bed.*dorm/i,
    isDorm: true,
    image: "images/dorm-wide-room.jpg",
    images: [
      "images/dorm-wide-room.jpg",
      "images/dorm-main.jpg",
      "images/dorm-bunk-locker.jpg",
      "images/dorm-lockers.jpg",
      "images/bathroom-showers.jpg",
    ],
  },
  {
    id: "3",
    name: "8-Bed Female Dorm",
    slug: "8-bed-female-dorm",
    capacity: 8,
    description:
      "Female-only 8-bed dorm with privacy curtains, personal lights, and lockers. Beds 213–220.",
    details:
      "Reserved exclusively for female guests, this 8-bed dorm (beds 213–220) offers extra peace of mind. Each bed has a thick privacy curtain, reading light, USB charging, and personal shelf. Individual full-size lockers are provided. A dedicated shared bathroom is just around the corner.",
    features: ["Female only", "Privacy curtains", "Locker", "USB charging"],
    namePattern: /female|8.*bed.*fem/i,
    isDorm: true,
    image: "images/dorm-bed-detail.jpg",
    images: [
      "images/dorm-bed-detail.jpg",
      "images/dorm-bunk-locker.jpg",
      "images/dorm-lockers.jpg",
      "images/bathroom-showers.jpg",
    ],
  },
  {
    id: "4",
    name: "Private Double Room",
    slug: "private-double",
    capacity: 2,
    description:
      "Cosy private double room in the heart of Bratislava Old Town.",
    details:
      "Enjoy privacy in our cosy double room with a comfortable double bed, fresh linen, and soft ambient lighting. The room features a work desk, wardrobe, with clean shared bathrooms just down the hall. Located on the upper floor for a quieter stay. Perfect for couples or anyone wanting their own space in the heart of the Old Town.",
    features: ["Shared bathroom", "City centre", "Quiet", "Work desk"],
    namePattern: /double\s+room/i,
    isDorm: false,
    image: "images/private-bed-nightlight.jpg",
    images: [
      "images/private-bed-nightlight.jpg",
      "images/private-bed-headboard.jpg",
      "images/private-corner.jpg",
      "images/private-doorway.jpg",
      "images/bathroom-mirror.jpg",
    ],
  },
  {
    id: "5",
    name: "Private Twin Room",
    slug: "private-twin",
    capacity: 2,
    description:
      "Private twin room with two single beds, ideal for friends or colleagues.",
    details:
      "Two separate single beds in a private room — great for friends, siblings, or colleagues travelling together. The room includes a work desk and wardrobe; clean shared bathrooms are just down the hall. Natural skylight brings in plenty of daylight. Smart lock entry for 24/7 access.",
    features: ["Twin beds", "Shared bathroom", "City centre", "Skylight"],
    namePattern: /twin/i,
    isDorm: false,
    image: "images/private-bed-skylight.jpg",
    images: [
      "images/private-bed-skylight.jpg",
      "images/private-desk-chair.jpg",
      "images/private-corner.jpg",
      "images/bathroom-mirror.jpg",
    ],
  },
  {
    id: "6",
    name: "Private Quadruple Room",
    slug: "private-quadruple",
    capacity: 4,
    description:
      "Spacious private room for up to four guests, with shared bathrooms nearby.",
    details:
      "Our largest private room comfortably accommodates up to 4 guests with a mix of beds. Includes a spacious work area and wardrobe; clean shared bathrooms are nearby. Ideal for families or small groups who want their own space. Ground floor access with plenty of natural light.",
    features: ["Quadruple", "Shared bathroom", "City centre", "Spacious"],
    namePattern: /quad|quadruple/i,
    isDorm: false,
    image: "images/private-workspace.jpg",
    images: [
      "images/private-workspace.jpg",
      "images/private-bed-headboard.jpg",
      "images/private-doorway.jpg",
      "images/bathroom-mirror.jpg",
    ],
  },
];

/** Match Cloudbeds room type name to our display config (static only) */
export function matchRoomDisplayConfig(
  cloudbedsName: string,
): RoomType | undefined {
  return ROOM_TYPES.find((rt) => rt.namePattern.test(cloudbedsName));
}

// ---------------------------------------------------------------------------
// Dynamic room types (populated at runtime from API)
// ---------------------------------------------------------------------------

let _dynamicRoomTypes: RoomType[] | null = null;

/** Set dynamic room types fetched from API */
export function setDynamicRoomTypes(types: RoomType[]): void {
  _dynamicRoomTypes = types;
}

/** Get active room types (dynamic if available, otherwise static constant) */
export function getActiveRoomTypes(): RoomType[] {
  return _dynamicRoomTypes ?? ROOM_TYPES;
}

/** Match Cloudbeds room type name — checks dynamic types first, then static */
export function matchRoomDisplayConfigDynamic(
  cloudbedsName: string,
): RoomType | undefined {
  const types = getActiveRoomTypes();
  return types.find((rt) => rt.namePattern.test(cloudbedsName));
}

// ---------------------------------------------------------------------------
// Pricing & policy
// ---------------------------------------------------------------------------

/** City tax per guest per night (EUR) */
export const CITY_TAX_PER_NIGHT = 3.5;

/** Check-in time (24h format) */
export const CHECKIN_TIME = "14:00";

/** Check-out time (24h format) */
export const CHECKOUT_TIME = "10:00";

// ---------------------------------------------------------------------------
// Forgotten items shipping
// ---------------------------------------------------------------------------

export const EU_COUNTRIES = [
  { value: "SK", label: "Slovakia" },
  { value: "CZ", label: "Czech Republic" },
  { value: "AT", label: "Austria" },
  { value: "HU", label: "Hungary" },
  { value: "PL", label: "Poland" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "IT", label: "Italy" },
  { value: "ES", label: "Spain" },
  { value: "NL", label: "Netherlands" },
  { value: "BE", label: "Belgium" },
  { value: "PT", label: "Portugal" },
  { value: "SE", label: "Sweden" },
  { value: "DK", label: "Denmark" },
  { value: "FI", label: "Finland" },
  { value: "IE", label: "Ireland" },
  { value: "HR", label: "Croatia" },
  { value: "SI", label: "Slovenia" },
  { value: "RO", label: "Romania" },
  { value: "BG", label: "Bulgaria" },
  { value: "GR", label: "Greece" },
  { value: "LT", label: "Lithuania" },
  { value: "LV", label: "Latvia" },
  { value: "EE", label: "Estonia" },
  { value: "LU", label: "Luxembourg" },
  { value: "GB", label: "United Kingdom" },
  { value: "CH", label: "Switzerland" },
  { value: "NO", label: "Norway" },
] as const;

export const PACKAGE_SIZES = [
  { value: "S", label: "Small", description: "Fits in a shoebox (up to 1 kg)" },
  { value: "M", label: "Medium", description: "Fits in a moving box (up to 5 kg)" },
  { value: "L", label: "Large", description: "Bigger items (up to 10 kg)" },
] as const;

export const FORGOTTEN_ITEMS_SERVICE_FEE = 5.0;
export const FORGOTTEN_ITEMS_PACKAGING_FEE = 2.0;
